import React, { useState, useEffect, useRef } from 'react'
import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from '@fullcalendar/interaction'
import axios from 'axios'
import Modal from 'react-bootstrap/Modal'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import { Spinner } from 'react-bootstrap'
import { useHistory } from 'react-router-dom'
import styled from 'styled-components'

const CloseButton = styled.button`
  position: absolute;
  top: 0;
  right: 6px;
  background: transparent;
  border: none;
  font-size: 20px;
  color: #aaa;
  cursor: pointer;

  &:hover {
    color: #333;
  }
`

const PlaceCalendar = ({ auth }) => {
  const history = useHistory()
  const [events, setEvents] = useState([])
  const [modalType, setModalType] = useState('block')
  const [selectedEvent, setSelectedEvent] = useState(null)

  const calendarRef = useRef(null)
  const enterKeyPressedRef = useRef(false)

  const [formData, setFormData] = useState({
    startDate: '',
    endDate: '',
    note: '',
  })
  const [showModal, setShowModal] = useState(false)
  const [placeDetails, setPlaceDetails] = useState(null)
  const [loading, setLoading] = useState(true)
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)
  const [confirmationAction, setConfirmationAction] = useState(null)

  const buttonRef = useRef(null) // Create a ref for the update button

  const fetchCalendarData = async () => {
    setLoading(true)
    try {
      const placeResponse = await axios.get('/api/places')
      const place = placeResponse.data?.place || null

      if (!place) {
        console.error('No place found for the user.')
        setLoading(false)
        return
      }

      setPlaceDetails(place)
      const response = await axios.get(`/api/calendar/${place.id}`)
      const { availability, bookings } = response.data

      const formattedEvents = [
        ...availability.map((item) => {
          const endDateWithTime = new Date(item.endDate)
          endDateWithTime.setHours(20, 0, 0, 0)

          return {
            id: item.id,
            title: item.note || 'Unavailable',
            start: new Date(item.startDate),
            end: endDateWithTime,
            backgroundColor: 'gray',
            allDay: false,
            extendedProps: { type: 'block', note: item.note },
          }
        }),
        ...bookings.map((item) => {
          const endDateWithTime = new Date(item.end_date)
          endDateWithTime.setHours(20, 0, 0, 0)

          let backgroundColor
          let title

          switch (item.status) {
            case 'pending':
              backgroundColor = '#eec751'
              title = 'Pending Request'
              break
            case 'approved':
              backgroundColor = '#50A94D'
              title = 'Approved Booking'
              break
            case 'completed':
              backgroundColor = '#50A94D'
              title = 'Completed Booking'
              break
            default:
              backgroundColor = 'red'
              title = 'Unknown Status'
              break
          }

          return {
            id: item.id,
            title: title,
            start: new Date(item.start_date),
            end: endDateWithTime,
            backgroundColor: backgroundColor,
            allDay: false,
            extendedProps: { type: 'booking', status: item.status },
          }
        }),
      ]

      setEvents(formattedEvents)
    } catch (error) {
      console.error('Error fetching calendar data:', error)
    } finally {
      setLoading(false)
    }
  }

  const confirmAction = (action) => {
    setConfirmationAction(() => action)
    setShowConfirmationModal(true)
  }

  const executeConfirmedAction = async () => {
    if (confirmationAction) await confirmationAction()
    setConfirmationAction(null)
    setShowConfirmationModal(false)
  }

  const handleSave = async () => {
    const currentEvents = events
    const startDate = new Date(formData.startDate)
    const endDate = new Date(formData.endDate)
    endDate.setDate(endDate.getDate() + 1)

    const today = new Date()
    today.setHours(0, 0, 0, 0)

    if (startDate < today || endDate < today) {
      alert('You cannot add events in the past.')
      return
    }

    try {
      const response = await axios.post(
        `/api/calendar/${placeDetails.id}/availability`,
        {
          startDate: formData.startDate,
          endDate: formData.endDate,
          note: formData.note,
        }
      )

      const newEvent = {
        id: response.data.data.id,
        title: formData.note || 'Unavailable',
        start: new Date(new Date(formData.startDate).setHours(10, 0, 0, 0)),
        end: new Date(new Date(formData.endDate).setHours(20, 0, 0, 0)),
        backgroundColor: 'gray',
        extendedProps: { type: 'block', note: formData.note },
      }

      setEvents((prevEvents) => {
        const updatedEvents = [...prevEvents, newEvent]
        return updatedEvents
      })

      setFormData({
        startDate: '',
        endDate: '',
        note: '',
      })
      setShowModal(false)
      if (calendarRef.current) {
        calendarRef.current.getApi().refetchEvents()
      }
    } catch (error) {
      console.error('Error saving blocked date:', error)
    }
  }

  const handleRemove = () => {
    confirmAction(async () => {
      try {
        await axios.delete(
          `/api/calendar/${placeDetails.id}/availability/${selectedEvent.id}`
        )

        setEvents((prevEvents) => {
          const updatedEvents = prevEvents.filter(
            (event) => String(event.id) !== String(selectedEvent.id)
          )
          return updatedEvents
        })

        if (calendarRef.current) {
          const calendarApi = calendarRef.current.getApi()
          const event = calendarApi.getEventById(selectedEvent.id)
          if (event) {
            event.remove() // Clear from FullCalendar
          }
          calendarApi.refetchEvents() // Force a refresh of the view
        }

        setSelectedEvent(null)
        setShowModal(false)
      } catch (error) {
        console.error('Error removing blocked date:', error)
      }
    })
  }

  const handleUpdate = () => {
    confirmAction(async () => {
      try {
        const endDate = new Date(formData.endDate)
        endDate.setHours(20, 0, 0, 0) // Set end time to 8 PM
        const today = new Date()
        today.setHours(0, 0, 0, 0)

        if (endDate < today) {
          alert('You cannot update events that have already ended.')
          return
        }

        // Update the event in the backend
        await axios.put(
          `/api/calendar/${placeDetails.id}/availability/${selectedEvent.id}`,
          {
            startDate: formData.startDate,
            endDate: formData.endDate,
            note: formData.note,
          }
        )

        const updatedStartDate = new Date(formData.startDate)
        const updatedEndDate = new Date(formData.endDate)
        updatedStartDate.setHours(8, 0, 0, 0)
        updatedEndDate.setHours(20, 0, 0, 0)

        setEvents((prevEvents) =>
          prevEvents.map((event) =>
            String(event.id) === String(selectedEvent.id)
              ? {
                  ...event,
                  start: updatedStartDate,
                  end: updatedEndDate,
                  title: formData.note || 'Unavailable',
                  extendedProps: {
                    ...event.extendedProps,
                    note: formData.note,
                  },
                }
              : event
          )
        )

        if (calendarRef.current) {
          const calendarApi = calendarRef.current.getApi()
          const event = calendarApi.getEventById(selectedEvent.id)
          if (event) {
            event.setStart(updatedStartDate)
            event.setEnd(updatedEndDate)
            event.setProp('title', formData.note || 'Unavailable')
            event.setExtendedProp('note', formData.note)
          }
          calendarApi.refetchEvents()
        }

        setSelectedEvent(null)
        setShowModal(false)
      } catch (error) {
        console.error('Error updating blocked date:', error)
      }
    })
  }

  const handleDateClick = (info) => {
    const currentEvents = events // Ensure the state is fresh
    const overlappingEvent = currentEvents.find(
      (event) =>
        new Date(event.start) <= new Date(info.dateStr) &&
        new Date(info.dateStr) <= new Date(event.end)
    )

    console.log('overlappingEvent', overlappingEvent)

    if (overlappingEvent) {
      if (overlappingEvent.extendedProps.type === 'booking') {
        history.push(`/dashboard/bookings?id=${overlappingEvent.id}`)
      } else {
        // Open modal to modify block or request
        setFormData({
          startDate: new Date(overlappingEvent.start)
            .toISOString()
            .split('T')[0],
          endDate: new Date(overlappingEvent.end).toISOString().split('T')[0],
          note: overlappingEvent.extendedProps.note || '',
        })
        setSelectedEvent(overlappingEvent)
        setModalType('modify')
        setShowModal(true)
      }
      return
    }

    setFormData({
      startDate: info.dateStr,
      endDate: info.dateStr,
      note: '',
    })
    setModalType('block')
    setShowModal(true)
  }

  const handleEventClick = (info) => {
    const event = info.event
    if (event.extendedProps.type === 'booking') {
      history.push(`/dashboard/bookings?id=${event.id}`)
    } else {
      setFormData({
        startDate: new Date(event.start).toISOString().split('T')[0],
        endDate: new Date(event.end).toISOString().split('T')[0],
        note: event.extendedProps.note || '',
      })
      setSelectedEvent(event)
      setModalType('modify')
      setShowModal(true)
    }
  }

  useEffect(() => {
    fetchCalendarData()
  }, [auth])

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Enter' && showModal) {
        e.preventDefault() // Prevent default behavior
        if (buttonRef.current) {
          buttonRef.current.click() // Programmatically trigger the button click
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [showModal])

  const attachNavigationListeners = () => {
    const buttons = document.querySelectorAll('.fc-button')
    buttons.forEach((button) => {
      button.addEventListener('click', () => {
        console.log('click')
        if (button.disabled) {
          console.log(`Button ${button.getAttribute('title')} is disabled.`)
          return
        }
        button.disabled = true
        button.blur()
        console.log(
          `Action triggered by click: ${button.getAttribute('title')}`
        )

        setTimeout(() => {
          button.disabled = false
          console.log(`Button ${button.getAttribute('title')} re-enabled.`)
        }, 300)
      })

      button.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          if (button.disabled) {
            e.preventDefault()
            console.log(
              `Enter suppressed for button: ${button.getAttribute('title')}`
            )
            return
          }
          button.disabled = true
          button.blur()
          e.preventDefault()
          console.log(
            `Action triggered by Enter key: ${button.getAttribute('title')}`
          )

          setTimeout(() => {
            button.disabled = false
            console.log(`Button ${button.getAttribute('title')} re-enabled.`)
          }, 300)
        }
      })
    })
  }

  useEffect(() => {
    attachNavigationListeners()
  }, [events])

  return (
    <div>
      <div style={{ marginTop: '40px' }}>
        <div className="text-center">
          <h1>Place Calendar</h1>
        </div>
      </div>
      {loading ? (
        <div style={{ textAlign: 'center' }}>
          <Spinner animation="border" role="status" className="me-2" />
          <p>Loading calendar data...</p>
        </div>
      ) : (
        <>
          {placeDetails && (
            <div>
              <h2>
                Property Details: {placeDetails.typePlace || 'Your Property'}
              </h2>
              <ul>
                <li>
                  <strong>Description:</strong>{' '}
                  {placeDetails.placeDescription ||
                    'You haven’t added a description yet. Add one to attract more guests!'}
                </li>
              </ul>
            </div>
          )}
          {placeDetails && (
            <FullCalendar
              ref={calendarRef}
              plugins={[dayGridPlugin, interactionPlugin]}
              initialView="dayGridMonth"
              events={events}
              dateClick={handleDateClick}
              displayEventTime={false}
              eventClick={handleEventClick}
              validRange={{
                start: new Date().toISOString().split('T')[0],
              }}
              aspectRatio={1.5}
              height="auto"
            />
          )}
          <Modal show={showModal} onHide={() => setShowModal(false)} centered>
            <Modal.Header>
              <Modal.Title>
                {modalType === 'block'
                  ? 'Block Out Dates'
                  : 'Modify Blocked Dates'}
              </Modal.Title>
              <CloseButton onClick={() => setShowModal(false)}>
                &times;
              </CloseButton>
            </Modal.Header>
            <Modal.Body>
              <Form>
                <Form.Group className="mb-3">
                  <Form.Label>Start Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={formData.startDate}
                    dateFormat="yyyy-MM-dd"
                    placeholderText="Select a date"
                    className="form-control"
                    onFocus={(e) =>
                      e.target.showPicker && e.target.showPicker()
                    }
                    onChange={(e) =>
                      setFormData({ ...formData, startDate: e.target.value })
                    }
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>End Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={formData.endDate}
                    onFocus={(e) =>
                      e.target.showPicker && e.target.showPicker()
                    }
                    dateFormat="yyyy-MM-dd"
                    placeholderText="Select a date"
                    className="form-control"
                    onChange={(e) =>
                      setFormData({ ...formData, endDate: e.target.value })
                    }
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Note</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter a note (e.g., Holiday in London)"
                    value={formData.note}
                    onChange={(e) =>
                      setFormData({ ...formData, note: e.target.value })
                    }
                  />
                </Form.Group>
              </Form>
            </Modal.Body>
            <Modal.Footer>
              {modalType === 'block' ? (
                <Button variant="primary" onClick={handleSave} ref={buttonRef}>
                  Save
                </Button>
              ) : (
                <>
                  <Button variant="danger" onClick={handleRemove}>
                    Remove from Calendar
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleUpdate}
                    ref={buttonRef}
                  >
                    Update
                  </Button>
                </>
              )}
            </Modal.Footer>
          </Modal>
          <Modal
            show={showConfirmationModal}
            onHide={() => setShowConfirmationModal(false)}
            centered
          >
            <Modal.Header>
              <Modal.Title>Confirm Action</Modal.Title>
              <CloseButton onClick={() => setShowConfirmationModal(false)}>
                &times;
              </CloseButton>
            </Modal.Header>
            <Modal.Body>
              Are you sure you want to proceed with this action?
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={() => setShowConfirmationModal(false)}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                ref={buttonRef}
                onClick={executeConfirmedAction}
              >
                Confirm
              </Button>
            </Modal.Footer>
          </Modal>
        </>
      )}
    </div>
  )
}

export default PlaceCalendar
